
import { useEffect, useState, useRef } from 'react';

interface Candle {
  x: number;
  open: number;
  close: number;
  high: number;
  low: number;
  bullish: boolean;
}

interface Ticker {
  symbol: string;
  price: string;
  change: string;
  positive: boolean;
  x: number;
  y: number;
  speed: number;
}

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
}

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false);
  const [typedText, setTypedText] = useState('');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animFrameRef = useRef<number>(0);
  const fullName = 'Илиев Николай Андреевич';

  // Typing effect
  useEffect(() => {
    setIsVisible(true);
    let currentIndex = 0;
    const typingInterval = setInterval(() => {
      if (currentIndex <= fullName.length) {
        setTypedText(fullName.slice(0, currentIndex));
        currentIndex++;
      } else {
        clearInterval(typingInterval);
      }
    }, 80);
    return () => clearInterval(typingInterval);
  }, []);

  // Canvas financial animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    // Generate candles
    const generateCandles = (
      count: number,
      startX: number,
      baseY: number,
      spread: number
    ): Candle[] => {
      const candles: Candle[] = [];
      let price = baseY;
      for (let i = 0; i < count; i++) {
        const change = (Math.random() - 0.48) * spread;
        const open = price;
        const close = price + change;
        const high = Math.max(open, close) + Math.random() * spread * 0.5;
        const low = Math.min(open, close) - Math.random() * spread * 0.5;
        candles.push({
          x: startX + i * 18,
          open,
          close,
          high,
          low,
          bullish: close > open,
        });
        price = close;
      }
      return candles;
    };

    // Chart line points
    const generateLine = (
      count: number,
      startX: number,
      startY: number,
      spread: number
    ) => {
      const pts: { x: number; y: number }[] = [];
      let y = startY;
      for (let i = 0; i < count; i++) {
        y += (Math.random() - 0.47) * spread;
        pts.push({ x: startX + i * 8, y });
      }
      return pts;
    };

    // Tickers
    const tickers: Ticker[] = [
      {
        symbol: 'BTC/USD',
        price: '67,420',
        change: '+2.34%',
        positive: true,
        x: 0,
        y: 0.12,
        speed: 0.3,
      },
      {
        symbol: 'S&P 500',
        price: '5,248',
        change: '+0.87%',
        positive: true,
        x: 0.2,
        y: 0.88,
        speed: 0.25,
      },
      {
        symbol: 'GOLD',
        price: '2,318',
        change: '+1.12%',
        positive: true,
        x: 0.5,
        y: 0.07,
        speed: 0.35,
      },
      {
        symbol: 'EUR/USD',
        price: '1.0842',
        change: '-0.23%',
        positive: false,
        x: 0.7,
        y: 0.92,
        speed: 0.28,
      },
      {
        symbol: 'NASDAQ',
        price: '18,340',
        change: '+1.45%',
        positive: true,
        x: 0.85,
        y: 0.15,
        speed: 0.22,
      },
      {
        symbol: 'OIL/WTI',
        price: '78.54',
        change: '-0.67%',
        positive: false,
        x: 0.1,
        y: 0.5,
        speed: 0.18,
      },
      {
        symbol: 'ETH/USD',
        price: '3,512',
        change: '+3.21%',
        positive: true,
        x: 0.9,
        y: 0.55,
        speed: 0.32,
      },
    ];

    // Particles
    const particles: Particle[] = Array.from({ length: 60 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.4,
      vy: (Math.random() - 0.5) * 0.4,
      size: Math.random() * 1.5 + 0.5,
      opacity: Math.random() * 0.4 + 0.1,
    }));

    let candles1 = generateCandles(60, 0, canvas.height * 0.55, 12);
    let candles2 = generateCandles(40, canvas.width * 0.45, canvas.height * 0.35, 8);
    let line1 = generateLine(120, 0, canvas.height * 0.65, 6);
    let line2 = generateLine(80, canvas.width * 0.3, canvas.height * 0.25, 10);
    let line3 = generateLine(100, canvas.width * 0.6, canvas.height * 0.75, 7);

    let offset = 0;
    let tickerOffsets = tickers.map((t) => t.x * canvas.width);

    const drawGrid = () => {
      ctx.strokeStyle = 'rgba(212,175,55,0.04)';
      ctx.lineWidth = 1;
      const cols = 20;
      const rows = 12;
      for (let i = 0; i <= cols; i++) {
        const x = (canvas.width / cols) * i;
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      for (let i = 0; i <= rows; i++) {
        const y = (canvas.height / rows) * i;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };

    const drawCandles = (candles: Candle[], alpha: number, offsetX: number) => {
      candles.forEach((c) => {
        const x = c.x + offsetX;
        if (x < -20 || x > canvas.width + 20) return;
        const color = c.bullish
          ? `rgba(212,175,55,${alpha})`
          : `rgba(180,60,60,${alpha * 0.7})`;
        ctx.strokeStyle = color;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(x, c.high);
        ctx.lineTo(x, c.low);
        ctx.stroke();
        ctx.fillStyle = color;
        const bodyH = Math.abs(c.close - c.open);
        ctx.fillRect(x - 4, Math.min(c.open, c.close), 8, Math.max(bodyH, 2));
      });
    };

    const drawLine = (
      pts: { x: number; y: number }[],
      color: string,
      lineWidth: number,
      offsetX: number
    ) => {
      if (pts.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = color;
      ctx.lineWidth = lineWidth;
      ctx.lineJoin = 'round';
      let started = false;
      pts.forEach((p) => {
        const x = p.x + offsetX;
        if (x < -10 || x > canvas.width + 10) return;
        if (!started) {
          ctx.moveTo(x, p.y);
          started = true;
        } else ctx.lineTo(x, p.y);
      });
      ctx.stroke();

      // Glow fill under line
      const visiblePts = pts.filter(
        (p) => p.x + offsetX >= -10 && p.x + offsetX <= canvas.width + 10
      );
      if (visiblePts.length > 1) {
        ctx.beginPath();
        ctx.moveTo(visiblePts[0].x + offsetX, visiblePts[0].y);
        visiblePts.forEach((p) =>
          ctx.lineTo(p.x + offsetX, p.y)
        );
        ctx.lineTo(
          visiblePts[visiblePts.length - 1].x + offsetX,
          canvas.height
        );
        ctx.lineTo(visiblePts[0].x + offsetX, canvas.height);
        ctx.closePath();
        const grad = ctx.createLinearGradient(0, 0, 0, canvas.height);
        grad.addColorStop(0, 'rgba(212,175,55,0.06)');
        grad.addColorStop(1, 'rgba(212,175,55,0)');
        ctx.fillStyle = grad;
        ctx.fill();
      }
    };

    const drawTickers = (t: number) => {
      tickers.forEach((ticker, i) => {
        tickerOffsets[i] = (tickerOffsets[i] + ticker.speed) % (canvas.width + 300);
        const x = tickerOffsets[i] - 150;
        const y = ticker.y * canvas.height;
        ctx.font = "bold 11px 'Inter', monospace";
        ctx.fillStyle = ticker.positive
          ? 'rgba(212,175,55,0.55)'
          : 'rgba(200,80,80,0.45)';
        ctx.fillText(`${ticker.symbol}  ${ticker.price}  ${ticker.change}`, x, y);
      });
    };

    const drawParticles = () => {
      particles.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${p.opacity})`;
        ctx.fill();
      });
    };

    const drawPriceLabels = (offsetX: number) => {
      const prices = ['67,420', '5,248', '2,318', '1.0842', '18,340'];
      prices.forEach((price, i) => {
        const x = (canvas.width / prices.length) * i + 60 + offsetX * 0.1;
        const y = canvas.height * (0.2 + i * 0.12);
        if (x < 0 || x > canvas.width) return;
        ctx.font = "10px 'Inter', monospace";
        ctx.fillStyle = 'rgba(212,175,55,0.18)';
        ctx.fillText(price, x % canvas.width, y);
      });
    };

    const animate = (t: number) => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Background gradient
      const bg = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      bg.addColorStop(0, '#0A1628');
      bg.addColorStop(0.5, '#0D1B35');
      bg.addColorStop(1, '#050B14');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      drawGrid();

      offset = t * 0.015;

      // Scrolling candles
      drawCandles(candles1, 0.22, -(offset * 0.8) % (60 * 18));
      drawCandles(candles2, 0.15, -(offset * 0.5) % (40 * 18));

      // Chart lines
      drawLine(line1, 'rgba(212,175,55,0.35)', 1.5, -(offset * 1.2) % (120 * 8));
      drawLine(line2, 'rgba(100,200,150,0.2)', 1, -(offset * 0.8) % (80 * 8));
      drawLine(line3, 'rgba(212,175,55,0.18)', 1, -(offset * 0.6) % (100 * 8));

      drawPriceLabels(offset);
      drawTickers(t);
      drawParticles();

      animFrameRef.current = requestAnimationFrame(animate);
    };

    animFrameRef.current = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  const scrollToAbout = () => {
    const aboutSection = document.getElementById('about');
    aboutSection?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Canvas financial animation */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full"
        style={{ zIndex: 0 }}
      />

      {/* Radial vignette overlay */}
      <div
        className="absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse at center, rgba(10,22,40,0.3) 0%, rgba(5,11,20,0.75) 100%)',
          zIndex: 1,
        }}
      />

      {/* Photo glow behind */}
      <div
        className="absolute right-[8%] top-1/2 -translate-y-1/2 w-[420px] h-[420px] rounded-full"
        style={{
          background:
            'radial-gradient(circle, rgba(212,175,55,0.18) 0%, transparent 70%)',
          zIndex: 2,
          filter: 'blur(40px)',
        }}
      />

      <div className="container mx-auto px-6 lg:px-12 relative" style={{ zIndex: 3 }}>
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-20">
          {/* Left content */}
          <div className="flex-1 text-left max-w-2xl">
            <div
              className={`transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              <span className="text-xs uppercase tracking-[4px] text-[#D4AF37] font-semibold mb-4 block">
                Финансови пазари · Управление на капитал
              </span>
            </div>

            <h1
              className={`text-5xl lg:text-7xl font-bold text-white mb-6 font-['Playfair_Display'] transition-all duration-1000 delay-200 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
            >
              {typedText}
              <span className="animate-pulse text-[#D4AF37]">|</span>
            </h1>

            {/* Golden line */}
            <div
              className={`h-0.5 bg-gradient-to-r from-[#D4AF37] to-transparent mb-8 transition-all duration-1000 delay-500 ${
                isVisible ? 'w-32' : 'w-0'
              }`}
            />

            <p
              className={`text-xl lg:text-2xl text-white/80 font-light mb-4 font-['Inter'] transition-all duration-1000 delay-300 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              Професионален участник на финансовите пазари
            </p>
            <p
              className={`text-base text-[#D4AF37]/70 font-['Inter'] mb-10 transition-all duration-1000 delay-400 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              10+ години опит · МИРЭА + London Business School
            </p>

            {/* Stats row */}
            <div
              className={`flex gap-8 mb-10 transition-all duration-1000 delay-500 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
            >
              {[
                { val: '10+', label: 'години опит' },
                { val: '2016', label: 'старт' },
                { val: '∞', label: 'класове активи' },
              ].map((s, i) => (
                <div key={i} className="text-center">
                  <div className="text-2xl font-bold text-[#D4AF37] font-['Playfair_Display']">
                    {s.val}
                  </div>
                  <div className="text-xs text-white/50 uppercase tracking-wider mt-1">
                    {s.label}
                  </div>
                </div>
              ))}
            </div>

            <a
              href="https://t.me/NikLiev"
              target="_blank"
              rel="noopener noreferrer"
              className={`inline-block px-10 py-4 border-2 border-[#D4AF37] text-[#D4AF37] rounded-full font-medium text-lg transition-all duration-300 hover:bg-[#D4AF37] hover:text-[#0A1628] hover:scale-105 hover:shadow-[0_0_40px_rgba(212,175,55,0.6)] cursor-pointer whitespace-nowrap ${
                isVisible ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ transitionDelay: '700ms' }}
            >
              <i className="ri-telegram-line mr-2"></i>
              Свържете се
            </a>
          </div>

          {/* Right content - Photo with financial overlay */}
          <div
            className={`flex-1 flex justify-center lg:justify-end transition-all duration-1000 delay-300 ${
              isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
            }`}
          >
            <div className="relative">
              {/* Outer ring glow */}
              <div className="absolute -inset-6 rounded-full border border-[#D4AF37]/20 animate-pulse" />
              <div
                className="absolute -inset-12 rounded-full border border-[#D4AF37]/10"
                style={{ animation: 'spin 20s linear infinite' }}
              />

              {/* Chart line overlay on photo */}
              <div
                className="absolute -bottom-4 -left-8 -right-8 h-20 overflow-hidden rounded-b-3xl"
                style={{ zIndex: 5 }}
              >
                <svg viewBox="0 0 300 60" className="w-full h-full" preserveAspectRatio="none">
                  <polyline
                    points="0,50 30,40 60,45 90,25 120,30 150,15 180,20 210,10 240,18 270,8 300,12"
                    fill="none"
                    stroke="rgba(212,175,55,0.7)"
                    strokeWidth="2"
                  />
                  <polygon
                    points="0,50 30,40 60,45 90,25 120,30 150,15 180,20 210,10 240,18 270,8 300,12 300,60 0,60"
                    fill="rgba(212,175,55,0.08)"
                  />
                </svg>
              </div>

              {/* Floating ticker badges */}
              <div
                className="absolute -top-4 -right-6 bg-[#0A1628]/90 border border-[#D4AF37]/50 rounded-lg px-3 py-1.5 backdrop-blur-sm"
                style={{ zIndex: 6 }}
              >
                <span className="text-[#D4AF37] text-xs font-bold font-mono">BTC +2.34%</span>
              </div>
              <div
                className="absolute top-1/3 -left-10 bg-[#0A1628]/90 border border-[#D4AF37]/30 rounded-lg px-3 py-1.5 backdrop-blur-sm"
                style={{ zIndex: 6 }}
              >
                <span className="text-green-400 text-xs font-bold font-mono">S&P500 ▲</span>
              </div>
              <div
                className="absolute bottom-8 -right-8 bg-[#0A1628]/90 border border-[#D4AF37]/30 rounded-lg px-3 py-1.5 backdrop-blur-sm"
                style={{ zIndex: 6 }}
              >
                <span className="text-[#D4AF37] text-xs font-bold font-mono">GOLD 2,318</span>
              </div>

              {/* Photo */}
              <div
                className="relative w-80 h-80 lg:w-96 lg:h-96 rounded-full border-2 border-[#D4AF37] overflow-hidden shadow-[0_0_80px_rgba(212,175,55,0.35)]"
                style={{ zIndex: 4 }}
              >
                <img
                  src="https://static.readdy.ai/image/5490f3a4ad0e9eb55a0769efce385370/f4312741e15b4c57f2cbf5864dad409f.jpeg"
                  alt="Илиев Николай Андреевич"
                  className="w-full h-full object-cover object-top"
                  style={{ filter: 'brightness(1.05) contrast(1.1)' }}
                />
                {/* Subtle gold overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-[#D4AF37]/15 to-transparent" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToAbout}
        className="absolute bottom-10 left-1/2 transform -translate-x-1/2 text-[#D4AF37] animate-bounce cursor-pointer hover:scale-110 transition-transform"
        aria-label="Превъртете надолу"
        style={{ zIndex: 3 }}
      >
        <i className="ri-arrow-down-line text-4xl"></i>
      </button>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </section>
  );
}
